import React, { useState, useEffect, useCallback } from 'react';
import apiClient from '../../../api/apiClient';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import ErrorBanner from '../../../components/ui/ErrorBanner';
import { st, colors } from '../shared/meetingStyles';
import Avatar from '../shared/Avatar';
import { FlagIcon } from '../shared/Icons';

export default function WrapUpSection({ session, onEnded }) {
  const [attendance, setAttendance] = useState([]);
  const [ratings, setRatings] = useState({});
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [ending, setEnding] = useState(false);

  const load = useCallback(async () => {
    if (!session) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const [attRes, ratingsRes] = await Promise.all([
        apiClient.get(`/meetings/sessions/${session.id}/attendance`),
        apiClient.get(`/meetings/sessions/${session.id}/ratings`),
      ]);
      setAttendance((attRes.data.attendance || []).filter(a => a.present !== 0 && a.present !== false));
      const map = {};
      for (const r of ratingsRes.data.ratings || []) map[r.user_id] = r.rating;
      setRatings(map);
      setNotes(session.notes || '');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => { load(); }, [load]);

  const setRating = async (userId, rating) => {
    setRatings(r => ({ ...r, [userId]: rating }));
    await apiClient.put(`/meetings/sessions/${session.id}/ratings/${userId}`, { rating });
  };

  const saveNotes = async () => {
    await apiClient.put(`/meetings/sessions/${session.id}`, { notes });
  };

  const endMeeting = async () => {
    if (!window.confirm('End this meeting? This will lock it as completed.')) return;
    setEnding(true);
    try {
      await saveNotes();
      await apiClient.post(`/meetings/sessions/${session.id}/end`);
      onEnded && onEnded();
    } finally {
      setEnding(false);
    }
  };

  if (!session) return <div style={st.card}><div style={st.emptyState}><FlagIcon size={28} style={{ color: colors.faint }} />Start the meeting to wrap up.</div></div>;
  if (loading) return <LoadingSpinner />;

  return (
    <div style={st.card}>
      {error && <ErrorBanner message={error} />}
      <div style={st.cardHeader}><h2 style={{ ...st.h2, display: 'flex', alignItems: 'center', gap: '0.5rem' }}><FlagIcon size={18} /> Wrap-up</h2></div>
      <div style={{ fontSize: '0.8rem', fontWeight: '800', color: colors.text, marginBottom: '0.6rem' }}>Rate this meeting (1-10)</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '1.1rem' }}>
        {attendance.map(a => (
          <div key={a.user_id} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.4rem 0.6rem',
            borderRadius: '0.6rem', background: colors.bgSubtle, border: `1px solid ${colors.border}`,
          }}>
            <Avatar name={a.name} size={22} />
            <span style={{ fontSize: '0.78rem', color: colors.text, fontWeight: '600' }}>{a.name}</span>
            <input type="number" min={1} max={10} value={ratings[a.user_id] ?? ''}
              onChange={e => setRating(a.user_id, Number(e.target.value))}
              style={{ ...st.input, width: '50px', textAlign: 'center', fontWeight: '700' }} />
          </div>
        ))}
      </div>
      <div style={{ fontSize: '0.8rem', fontWeight: '800', color: colors.text, marginBottom: '0.5rem' }}>Notes</div>
      <textarea value={notes} onChange={e => setNotes(e.target.value)} onBlur={saveNotes} style={{ ...st.textarea, width: '100%', marginBottom: '1.1rem' }} />
      <button onClick={endMeeting} disabled={ending} style={{ ...st.primaryBtn, background: `linear-gradient(135deg, ${colors.green}, #15803d)`, boxShadow: '0 2px 10px rgba(22,163,74,0.3)', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
        <FlagIcon size={13} />{ending ? 'Ending...' : 'End Meeting'}
      </button>
    </div>
  );
}
